import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter } from "lucide-react";
import axios from "axios";
import { getImageUrl } from "../../utils/imageUtils";
import { storePath } from "../../utils/storeUtils";
import { useCart } from "../../context/CartContext";

export const StoreFooter = ({ store, theme, platformInfo }) => {
  const [footerLinks, setFooterLinks] = useState([]);
  const { setCartOpen } = useCart();

  useEffect(() => {
    const fetchFooterPages = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pages/public`);
        if (Array.isArray(response.data)) {
          setFooterLinks(response.data.filter(p => p.show_in_footer && p.is_active));
        }
      } catch (error) {
        console.error("Failed to fetch footer pages:", error);
      }
    };
    fetchFooterPages();
  }, []);

  // Use dynamic colors from the theme/admin settings
  const footerBg = theme.primary_color || "#111111"; // Dynamic primary color for background
  const headerTextColor = theme.accent_color || "#EAB308"; // Dynamic accent color for headers
  const underlineColor = theme.secondary_color || "#2563EB"; // Dynamic secondary color for underlines
  const subTextColor = "rgba(255, 255, 255, 0.6)"; // Muted white for secondary text

  // Helper for store-specific routes — imported from storeUtils

  // Resolve Address
  const hasBusinessAddress = store?.business_address && Object.values(store.business_address).some(val => val !== "" && val !== "US");
  let displayAddress = "";
  if (hasBusinessAddress) {
    const { address, city, state, zipcode } = store.business_address;
    const parts = [address, city, state, zipcode].filter(Boolean);
    displayAddress = parts.join(", ");
  } else if (store?.address) {
    displayAddress = store.address;
  }

  return (
    <footer style={{ backgroundColor: footerBg, fontFamily: theme?.font_family || 'inherit' }} className="text-white py-16 px-6 sm:px-12 border-t border-white/5 relative overflow-hidden">
      <style dangerouslySetInnerHTML={{ __html: `
        footer *, footer h2, footer h3, footer p, footer a, footer span, footer div {
            font-family: inherit !important;
        }
      `}} />
      {/* Decorative gradient overlay to ensure readability if background is too solid */}
      <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-24 mb-16">

          {/* Column 1: Store Logo & Description */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            {store?.logo_url ? (
              <img
                src={getImageUrl(store.logo_url)}
                alt={store.business_name}
                className="max-h-24 w-auto object-contain hover:scale-105 transition-transform duration-300"
              />
            ) : (
              store?.show_business_name !== false && (
                <h2 className="text-2xl font-black tracking-tighter" style={{ color: headerTextColor }}>
                  {store?.business_name || "RESORTER"}
                </h2>
              )
            )}
            <p className="mt-6 text-sm leading-relaxed" style={{ color: subTextColor }}>
              {store?.business_description || "Providing premium products and exceptional service to our valued customers."}
            </p>
          </div>

          {/* Column 2: Connect with Us (Contact) */}
          <div className="flex flex-col gap-6">
            <div className="relative inline-block w-fit">
              <h3 className="text-[15px] font-black uppercase tracking-wider mb-2" style={{ color: headerTextColor }}>
                Connect with Us
              </h3>
              <div className="h-1 w-10" style={{ backgroundColor: underlineColor }}></div>
            </div>

            <div className="space-y-5 text-sm font-medium" style={{ color: subTextColor }}>
              {displayAddress && (
                <div className="flex items-start gap-3 group">
                  <MapPin className="w-4 h-4 text-white shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                  <span className="leading-relaxed hover:text-white transition-colors capitalize">{displayAddress}</span>
                </div>
              )}
              {store?.contact_phone && (
                <div className="flex items-center gap-3 group">
                  <Phone className="w-4 h-4 text-white shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="hover:text-white transition-colors">{store.contact_phone}</span>
                </div>
              )}
              {store?.contact_email && (
                <div className="flex items-center gap-3 group">
                  <Mail className="w-4 h-4 text-white shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="hover:text-white transition-colors truncate">{store.contact_email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Column 3: My Account (Links) */}
          <div className="flex flex-col gap-6">
            <div className="relative inline-block w-fit">
              <h3 className="text-[15px] font-black uppercase tracking-wider mb-2" style={{ color: headerTextColor }}>
                My Account
              </h3>
              <div className="h-1 w-10" style={{ backgroundColor: underlineColor }}></div>
            </div>

            <ul className="space-y-4 text-sm font-medium" style={{ color: subTextColor }}>
              <li><Link to={storePath(store?.subdomain, "/account")} className="hover:text-white transition-all hover:pl-1">My Account</Link></li>
              <li><Link to={storePath(store?.subdomain, "/contact")} className="hover:text-white transition-all hover:pl-1">Contact</Link></li>
              <li><button onClick={() => setCartOpen(true)} className="hover:text-white transition-all hover:pl-1 text-left">Shopping Cart</button></li>
              <li><Link to={storePath(store?.subdomain, "/shop")} className="hover:text-white transition-all hover:pl-1">Shop</Link></li>
            </ul>
          </div>

          {/* Column 4: Terms & Policies (Dynamic Links) */}
          <div className="flex flex-col gap-6">
            <div className="relative inline-block w-fit">
              <h3 className="text-[15px] font-black uppercase tracking-wider mb-2" style={{ color: headerTextColor }}>
                Terms & Policies
              </h3>
              <div className="h-1 w-10" style={{ backgroundColor: underlineColor }}></div>
            </div>

            <ul className="space-y-4 text-sm font-medium" style={{ color: subTextColor }}>
              {footerLinks.map(page => (
                <li key={page._id || page.id}>
                  <a href={`/${page.slug}.html`} className="hover:text-white transition-all hover:pl-1">
                    {page.title}
                  </a>
                </li>
              ))}
              {footerLinks.length === 0 && (
                <>

                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Credits Bar */}
        <div className="pt-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-10">

          <div className="flex flex-wrap md:flex-nowrap items-center justify-center md:justify-start gap-x-4 gap-y-4 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: subTextColor }}>
            {platformInfo?.footer_copyright_text ? (
              <span className="whitespace-nowrap">{platformInfo.footer_copyright_text}</span>
            ) : (
              <>
                <span className="whitespace-nowrap">{`© All rights reserved ${new Date().getFullYear()}`}</span>
                <span className="hidden md:block opacity-20 text-xl">|</span>

                <div className="flex items-center gap-4 whitespace-nowrap">
                  <span className="opacity-80">A product of</span>
                  {platformInfo?.platform_logo_url ? (
                    <img src={getImageUrl(platformInfo.platform_logo_url)} alt="Platform" className="h-8 w-auto object-contain brightness-0 invert opacity-100" />
                  ) : (
                    <span className="text-white font-black tracking-tighter text-[12px]">
                      INFINITY<span style={{ color: headerTextColor }}>URNS</span>
                    </span>
                  )}
                </div>
              </>
            )}

            <span className="hidden md:block opacity-20 text-xl">|</span>

            <div className="flex items-center gap-4 whitespace-nowrap">
              <span className="opacity-80">Designed & Developed by</span>
              <a href="https://codomotive.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
                <div className="relative w-8 h-8 flex items-center justify-center">
                  <div className="relative w-6 h-6 group-hover:scale-110 transition-transform duration-500">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#3182ce] fill-current animate-spin-slow absolute top-0 left-0 drop-shadow-lg">
                      <path d="M12,15.5 A3.5,3.5 0 0,1 8.5,12 A3.5,3.5 0 0,1 12,8.5 A3.5,3.5 0 0,1 15.5,12 A3.5,3.5 0 0,1 12,15.5 M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.03L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.47,5.32 14.87,5.07L14.49,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.51,2.42L9.13,5.07C8.53,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11.03C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.53,18.67 9.13,18.93L9.51,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.49,21.58L14.87,18.93C15.47,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
                    </svg>
                    <div className="absolute -top-1 -right-1 w-3 h-3 text-[#f6ad55] fill-current animate-spin-reverse-fast shadow-sm">
                      <svg viewBox="0 0 24 24">
                        <path d="M12,15.5 A3.5,3.5 0 0,1 8.5,12 A3.5,3.5 0 0,1 12,8.5 A3.5,3.5 0 0,1 15.5,12 A3.5,3.5 0 0,1 12,15.5 M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.03L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.47,5.32 14.87,5.07L14.49,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.51,2.42L9.13,5.07C8.53,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11.03C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.53,18.67 9.13,18.93L9.51,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.49,21.58L14.87,18.93C15.47,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <span className="text-white font-black tracking-[0.2em] text-[12px] group-hover:text-[#f6ad55] transition-colors duration-300">CODOMOTIVE</span>
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <PaymentIcon src="https://img.icons8.com/color/48/000000/discover.png" alt="Discover" />
            <PaymentIcon src="https://img.icons8.com/color/48/000000/mastercard.png" alt="Mastercard" />
            <PaymentIcon src="https://img.icons8.com/color/48/000000/visa.png" alt="Visa" />
            <PaymentIcon src="https://img.icons8.com/color/48/000000/amex.png" alt="Amex" />
            <PaymentIcon src="https://img.icons8.com/color/48/000000/stripe.png" alt="Stripe" />
          </div>
        </div>
      </div>
    </footer>
  );
};

const PaymentIcon = ({ src, alt }) => (
  <div className="bg-white rounded p-1 flex items-center justify-center w-12 h-8 shadow-sm hover:scale-110 transition-transform cursor-pointer">
    <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />
  </div>
);

export default StoreFooter;
